import {
  type ComponentProps,
  memo,
  type ReactElement,
  type SyntheticEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { type BackgroundProps } from 'react-imgix';

import { type Clip, ClipStatus } from '@air/api/types';
import { ImgixImage } from '@air/component-imgix-image';
import { cn } from '@air/utils-tailwind';

import { AssetGalleryCardFailedTranscode } from '~/components/Gallery/AssetGalleryCard/AssetGalleryCardFailedTranscode';
import { AssetGalleryCardProcessing } from '~/components/Gallery/AssetGalleryCard/AssetGalleryCardProcessing';
import { getCloudFrontUrlFromImgixUrl } from '~/constants/cloudfront';
import { getRoundedImgixDimensions } from '~/utils/getRoundedImgixDimensions';
import { isPreviewSrc } from '~/utils/isPreviewSrc';

export type GalleryAssetThumbnailImageProps = {
  /** URI for the image to be displayed in the clip thumbnail */
  src: string | undefined;
  /** URI for the animated image to be displayed on hover for a clip e.g an animated preview on video hover */
  animatedSrc?: string;
  /** width of the image that should be rendered in the gallery */
  width: number;
  /** height of the image that should be rendered in the gallery */
  height: number;
  /**
   * Imgix parameters
   */
  imgixParams?: BackgroundProps['imgixParams'];
  /** Called when the final CDN image has loaded (used to dismiss canvas import preview shimmer) */
  onFinalImageLoad?: () => void;
  /** Called with the browser-resolved currentSrc after the image loads — used to populate the drag preview cache */
  onSrcLoaded?: (currentSrc: string) => void;
} & Pick<Clip, 'status' | 'ext' | 'description'> &
  Pick<ComponentProps<'img'>, 'className'>;

export const GalleryAssetThumbnailImage = memo(
  ({
    animatedSrc,
    className,
    description,
    ext,
    height,
    imgixParams,
    onFinalImageLoad,
    onSrcLoaded,
    status,
    src,
    width,
  }: GalleryAssetThumbnailImageProps) => {
    const [isLoaded, setLoaded] = useState(false);

    const handleLoad = useCallback(
      (e?: SyntheticEvent<HTMLImageElement>) => {
        setLoaded(true);
        onFinalImageLoad?.();
        if (e?.currentTarget.currentSrc) {
          onSrcLoaded?.(e.currentTarget.currentSrc);
        }
      },
      [onFinalImageLoad, onSrcLoaded],
    );

    const imgixDimensions = useMemo(() => getRoundedImgixDimensions({ height, width }), [height, width]);

    if ([ClipStatus.failed, ClipStatus.nonTranscodable].includes(status)) {
      return <AssetGalleryCardFailedTranscode ext={ext} />;
    }

    if (!src) {
      return <AssetGalleryCardProcessing ext={ext} />;
    }

    const isPreviewSource = isPreviewSrc(src);

    const imageClassNames: ComponentProps<'img'>['className'] = cn(
      'block min-h-full w-full rounded-4 object-cover',
      isLoaded ? 'opacity-100' : 'opacity-0',
      className,
    );

    let image: ReactElement;

    if (animatedSrc) {
      // biome-ignore lint/performance/noImgElement: animatedSrc is a video preview URL, not eligible for Next.js Image optimization
      image = <img alt={description} className={imageClassNames} src={animatedSrc} />;
    } else if (isPreviewSource) {
      // No opacity transition — the shimmer handles the loading state, image renders at full opacity immediately
      image = (
        // biome-ignore lint/performance/noImgElement: preview src is a blob/data/edits-bucket URL, not eligible for Next.js Image optimization
        <img
          alt={description || ''}
          className={cn('block min-h-full w-full rounded-4 object-cover', className)}
          src={src}
        />
      );
    } else {
      image = (
        <ImgixImage
          alt={description || ''}
          className={imageClassNames}
          fallbackUrl={getCloudFrontUrlFromImgixUrl(src)}
          height={imgixDimensions.height}
          imgixParams={{
            cs: 'origin',
            fit: 'crop',
            ...imgixParams,
          }}
          onLoad={handleLoad}
          src={src}
          width={imgixDimensions.width}
        />
      );
    }

    if (isPreviewSource) {
      return (
        <div className="relative h-full w-full">
          {image}
          <div className={cn('shimmer-background absolute inset-0 rounded-4 opacity-40')} />
        </div>
      );
    }

    return image;
  },
);

GalleryAssetThumbnailImage.displayName = 'GalleryAssetThumbnailImage';
